import { memo, type ReactNode } from 'react';
import { Button, Flex, Text } from '@chakra-ui/react';
import { ChevronDownIcon, DownloadIcon } from '@chakra-ui/icons';
import { useSelector } from 'react-redux';

import Search from 'components/Icons/Search';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import { PLAN_TABLE_TOOLBAR_HEIGHT, PLAN_TEXT } from '../planPageRevampUi';

import PlanTimeFilterSegment, { PlanTimeFilterValue } from './PlanTimeFilterSegment';

interface Properties {
  hint?: string;
  timeAggregation: string;
  tableFilter: PlanTimeFilterValue;
  onTimeFilterChange: (value: PlanTimeFilterValue) => void;
  onDownload: () => void;
  isExporting?: boolean;
  leadingActions?: ReactNode;
}

const PlanDataTableToolbar = ({
  hint = 'Expand the table to drill into the values for each of your items',
  timeAggregation,
  tableFilter,
  onTimeFilterChange,
  onDownload,
  isExporting = false,
  leadingActions,
}: Properties) => {
  const themeColors = useSelector(getPlanPageThemeColors);

  return (
    <Flex
      h={PLAN_TABLE_TOOLBAR_HEIGHT}
      alignItems='stretch'
      justifyContent='space-between'
      flexShrink={0}
      overflow='hidden'
      borderBottom='1px solid'
      borderColor={themeColors.border}
    >
      <Flex alignItems='center' gap={2} pl={3} minW={0} flex='1'>
        <Search width={4} height={4} color={themeColors.textSecondary} />
        <Text {...PLAN_TEXT.labelSm} color={themeColors.textSecondary} noOfLines={1}>
          {hint}
        </Text>
      </Flex>

      <Flex alignItems='stretch' gap={1} flexShrink={0} h='100%'>
        {leadingActions}

        {timeAggregation !== 'Y' && (
          <Flex alignItems='center' gap={3} px={4} h='100%'>
            <PlanTimeFilterSegment
              variant='tableTime'
              value={tableFilter}
              onChange={onTimeFilterChange}
            />
          </Flex>
        )}

        <Button
          h='100%'
          minH={PLAN_TABLE_TOOLBAR_HEIGHT}
          px={3}
          gap={2}
          borderRadius={0}
          {...PLAN_TEXT.bodyMedium}
          color={themeColors.white}
          bg={themeColors.primary}
          leftIcon={<DownloadIcon boxSize={4} />}
          rightIcon={<ChevronDownIcon boxSize={5} />}
          _hover={{ bg: themeColors.primaryHover }}
          _active={{ bg: themeColors.primaryPressed }}
          flexShrink={0}
          isLoading={isExporting}
          loadingText='Downloading'
          onClick={onDownload}
          width='fit-content'
        >
          Download
        </Button>
      </Flex>
    </Flex>
  );
};

export default memo(PlanDataTableToolbar);
